import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import {
  applyOp,
  CRUNCH_OPS,
  normalizePuzzle,
  rejectionMessage,
  starRating,
  type CrunchOp,
  type CrunchStep,
  type NumberCrunchLevel,
  type NumberCrunchPuzzle,
  type NumberTile,
} from './types';
import './numberCrunch.css';

export default function NumberCrunchGame() {
  const [data, setData] = useState<{
    puzzle: NumberCrunchPuzzle;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<NumberCrunchLevel>('numberCrunch')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Number Crunch yet.');
          return;
        }
        const puzzle = normalizePuzzle(res.data);
        if (!puzzle) {
          setLoadError("Today's Number Crunch puzzle is malformed.");
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="numberCrunch" />;
  if (!data) {
    return (
      <GameShell gameId="numberCrunch">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('numberCrunch', data.dateKey)) {
    return (
      <FinishedDaily gameId="numberCrunch" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <NumberCrunchPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

/** Builder state machine: idle → first number → operator → second number. */
type Builder =
  | { kind: 'idle' }
  | { kind: 'first'; tileId: number }
  | { kind: 'op'; tileId: number; op: CrunchOp };

function NumberCrunchPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: NumberCrunchPuzzle;
  dateKey: string;
  isToday: boolean;
}) {
  const nextId = useRef(0);
  const makeTiles = useCallback(
    () =>
      puzzle.sourceNumbers.map<NumberTile>((value) => ({
        id: nextId.current++,
        value,
        isOriginal: true,
        isUsed: false,
      })),
    [puzzle],
  );

  const { restored, save } = useDailySnapshot<{
    tiles: NumberTile[];
    steps: CrunchStep[];
    hintUsed: boolean;
  }>('numberCrunch', dateKey);

  const [tiles, setTiles] = useState<NumberTile[]>(() => {
    if (!restored?.tiles?.length) return makeTiles();
    // Resume the id sequence so new result tiles can't collide with restored ones.
    nextId.current = Math.max(...restored.tiles.map((t) => t.id)) + 1;
    return restored.tiles;
  });
  const [steps, setSteps] = useState<CrunchStep[]>(() => restored?.steps ?? []);
  const [builder, setBuilder] = useState<Builder>({ kind: 'idle' });
  const [completed, setCompleted] = useState(false);
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);
  const [shake, setShake] = useState(false);
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(save, { tiles, steps, hintUsed }, !completed);

  const toastTimer = useRef<number | undefined>(undefined);
  const showToast = useCallback((msg: string, tone: 'info' | 'error' = 'info') => {
    setToast({ msg, tone });
    clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2000);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (completed) recordOutcome('numberCrunch', dateKey, 'won', { subtitle: `Hit ${puzzle.target}.` });
  }, [completed, dateKey]);

  const available = useMemo(() => tiles.filter((t) => !t.isUsed), [tiles]);

  const triggerShake = useCallback(() => {
    setShake(true);
    window.setTimeout(() => setShake(false), 450);
  }, []);

  const applyOperation = useCallback(
    (left: NumberTile, right: NumberTile, op: CrunchOp, result: number) => {
      const resultTileId = nextId.current++;
      setTiles((prev) => [
        ...prev.map((t) =>
          t.id === left.id || t.id === right.id ? { ...t, isUsed: true } : t,
        ),
        { id: resultTileId, value: result, isOriginal: false, isUsed: false },
      ]);
      setSteps((prev) => [
        ...prev,
        {
          leftValue: left.value,
          rightValue: right.value,
          op,
          result,
          leftTileId: left.id,
          rightTileId: right.id,
          resultTileId,
        },
      ]);
      setBuilder({ kind: 'idle' });

      if (result === puzzle.target) {
        setCompleted(true);
        return;
      }
      const off = Math.abs(result - puzzle.target);
      if (off > 0 && off <= 5) showToast(`So close! Off by ${off}`);
    },
    [puzzle.target, showToast],
  );

  const selectTile = useCallback(
    (tile: NumberTile) => {
      if (completed || tile.isUsed) return;

      if (builder.kind === 'idle') {
        setBuilder({ kind: 'first', tileId: tile.id });
        return;
      }
      if (builder.kind === 'first') {
        // Tapping the same tile clears; tapping another switches selection.
        setBuilder(
          tile.id === builder.tileId
            ? { kind: 'idle' }
            : { kind: 'first', tileId: tile.id },
        );
        return;
      }

      // Operator is set — this is the second operand.
      if (tile.id === builder.tileId) {
        setBuilder({ kind: 'idle' });
        return;
      }
      const first = tiles.find((t) => t.id === builder.tileId);
      if (!first) {
        setBuilder({ kind: 'idle' });
        return;
      }
      const result = applyOp(builder.op, first.value, tile.value);
      if (result === null) {
        triggerShake();
        showToast(rejectionMessage(builder.op), 'error');
        return;
      }
      applyOperation(first, tile, builder.op, result);
    },
    [completed, builder, tiles, applyOperation, triggerShake, showToast],
  );

  const selectOp = useCallback(
    (op: CrunchOp) => {
      if (completed) return;
      // Only meaningful once a first number is picked; switching ops is free.
      if (builder.kind === 'first' || builder.kind === 'op') {
        setBuilder({ kind: 'op', tileId: builder.tileId, op });
      }
    },
    [completed, builder],
  );

  const undo = useCallback(() => {
    setSteps((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setTiles((ts) =>
        ts
          .filter((t) => t.id !== last.resultTileId)
          .map((t) =>
            t.id === last.leftTileId || t.id === last.rightTileId
              ? { ...t, isUsed: false }
              : t,
          ),
      );
      setBuilder({ kind: 'idle' });
      return prev.slice(0, -1);
    });
  }, []);

  const resetAll = useCallback(() => {
    setTiles(makeTiles());
    setSteps([]);
    setBuilder({ kind: 'idle' });
    setCompleted(false);
  }, [makeTiles]);

  /** The hint surfaces the next step of the published solution. */
  const useHint = useCallback(() => {
    if (completed || hintUsed) return;
    const step = puzzle.solution[steps.length];
    if (!step) {
      showToast('No more hint steps');
      return;
    }
    setHintUsed(true);
    showToast(`Try: ${step.a} ${step.op} ${step.b} = ${step.result}`);
  }, [completed, hintUsed, puzzle.solution, steps.length, showToast]);

  const equationDisplay = useMemo(() => {
    if (builder.kind === 'idle') return '';
    const tile = tiles.find((t) => t.id === builder.tileId);
    if (!tile) return '';
    return builder.kind === 'first' ? `${tile.value}` : `${tile.value} ${builder.op}`;
  }, [builder, tiles]);

  const selectedTileId = builder.kind === 'idle' ? null : builder.tileId;
  const selectedOp = builder.kind === 'op' ? builder.op : null;
  const stars = starRating(tiles, puzzle.target);

  return (
    <GameShell
      gameId="numberCrunch"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${steps.length} step${
        steps.length === 1 ? '' : 's'
      }`}
    >
      <div className="nc-board">
        <div className="nc-target">
          <span className="nc-target-label">TARGET</span>
          <span className="nc-target-value">{puzzle.target}</span>
        </div>

        <div className={'nc-equation' + (shake ? ' is-shake' : '')}>
          {equationDisplay || <span className="nc-equation-hint">Pick a number</span>}
        </div>

        <div className="nc-tiles">
          {available.map((tile) => (
            <button
              key={tile.id}
              type="button"
              className={
                'nc-tile' +
                (tile.id === selectedTileId ? ' is-selected' : '') +
                (tile.isOriginal ? '' : ' is-derived') +
                (tile.value === puzzle.target ? ' is-target' : '')
              }
              onClick={() => selectTile(tile)}
              disabled={completed}
            >
              {tile.value}
            </button>
          ))}
        </div>

        <div className="nc-ops">
          {CRUNCH_OPS.map((op) => (
            <button
              key={op}
              type="button"
              className={'nc-op' + (op === selectedOp ? ' is-selected' : '')}
              onClick={() => selectOp(op)}
              disabled={completed || builder.kind === 'idle'}
              aria-label={`Operator ${op}`}
            >
              {op}
            </button>
          ))}
        </div>

        {steps.length > 0 && (
          <div className="nc-steps">
            {steps.map((s, i) => (
              <div key={i} className="nc-step">
                {s.leftValue} {s.op} {s.rightValue} = <strong>{s.result}</strong>
              </div>
            ))}
          </div>
        )}

        <div className="nc-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={undo}
            disabled={completed || steps.length === 0}
          >
            Undo
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={resetAll}
            disabled={completed || steps.length === 0}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn-accent"
            onClick={useHint}
            disabled={completed || hintUsed}
          >
            Hint
          </button>
        </div>

        {toast && <div className={`nc-toast tone-${toast.tone}`}>{toast.msg}</div>}

        {completed && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={`Hit ${puzzle.target} in ${steps.length} step${
              steps.length === 1 ? '' : 's'
            }. ${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}`}
          />
        )}
      </div>
    </GameShell>
  );
}
